import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  forwardRef,
  inject,
  Input,
  ViewChild,
  AfterViewInit,
  Injector,
} from '@angular/core';
import {
  ReactiveFormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
} from '@angular/forms';
import {
  NgbDateParserFormatter,
  NgbDateStruct,
  NgbDatepickerModule,
  NgbInputDatepicker,
  NgbDatepickerConfig,
} from '@ng-bootstrap/ng-bootstrap';
import { ControlValueAccessor } from '@ngneat/reactive-forms';

import { FULL_RX, isValidDateString } from '../utils/is-valid-date-string';
import { UsDateParserFormatter } from '../utils/us-date-parser';

@Component({
  selector: 'digital-platform-date-picker',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgbDatepickerModule],
  providers: [
    { provide: NgbDateParserFormatter, useClass: UsDateParserFormatter },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
  ],
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
})
export class DatePickerComponent
  implements ControlValueAccessor, AfterViewInit
{
  @Input() placeholder = 'MM/DD/YYYY';
  @Input() disabled = false;
  @Input() errors: { [key: string]: any } | null = null;
  @Input() touched = false;

  @ViewChild('dp', { static: true }) dp: NgbInputDatepicker | undefined;
  @ViewChild('dateInput', { static: true })
  inputRef: ElementRef<HTMLInputElement> | undefined;
  value = '';
  internalTouched = false;
  hasInvalidDate = false;

  private parserFormatter: NgbDateParserFormatter = inject(
    NgbDateParserFormatter
  );
  private injector = inject(Injector);

  private ngControl: NgControl | null = null;

  onChange = (value: string) => {};
  onTouched = () => {};

  constructor(config: NgbDatepickerConfig) {
    config.weekdays = 0;
    config.outsideDays = 'collapsed';
    config.firstDayOfWeek = 7;
  }

  ngAfterViewInit(): void {
    this.ngControl = this.injector.get(NgControl, null);
  }

  get disabledAttr() {
    return this.isDisabled ? '' : null;
  }

  get isTouched(): boolean {
    // Use NgControl if available, otherwise fall back to input
    const ngControlTouched = this.ngControl?.control?.touched || false;
    return this.internalTouched || this.touched || ngControlTouched;
  }

  get formControlErrors() {
    // Use NgControl if available, otherwise fall back to input
    return this.ngControl?.control?.errors || this.errors;
  }

  get isDisabled(): boolean {
    // Check both input property and FormControl disabled state
    return this.disabled || this.ngControl?.control?.disabled || false;
  }

  get hasAnyErrors(): boolean {
    if (this.isDisabled) return false;

    const errors: ValidationErrors | null = this.formControlErrors;
    return (
      (this.hasInvalidDate && this.isTouched) || !!(errors && this.isTouched)
    );
  }

  get errorMessage(): string | null {
    if (!this.isTouched) return null;

    if (this.hasInvalidDate) {
      return 'Please enter a valid date (MM/DD/YYYY)';
    }

    // External errors from Angular validators
    const errors = this.formControlErrors;
    if (errors) {
      if (errors['required']) {
        return 'This field is required';
      }

      if (errors['pattern']) {
        return 'Invalid format';
      }

      if (errors['futureDate']) {
        return 'Date must be in the past';
      }

      if (errors['notWeekend']) {
        return 'Date must be on a weekend';
      }

      if (errors['dateRange']) {
        return 'Date is outside the allowed range';
      }

      // Generic error message for other validators
      const firstErrorKey = Object.keys(errors)[0];
      const errorValue = errors[firstErrorKey];

      // If the error has a message property, use it
      if (errorValue && typeof errorValue === 'object' && errorValue.message) {
        return errorValue.message;
      }

      return `Invalid ${firstErrorKey}`;
    }

    return null;
  }

  get iconVariant(): string {
    if (this.isDisabled) {
      return 'disabled';
    }

    if (this.hasAnyErrors) {
      return 'warning';
    }

    return 'primary';
  }

  get inputErrorClass(): string | null {
    return this.hasAnyErrors ? 'form-control_error' : null;
  }

  get showError(): boolean {
    return this.hasAnyErrors;
  }

  writeValue(value: string): void {
    this.value = value || '';
    this.validateDate(this.value);
    if (this.inputRef) {
      this.inputRef.nativeElement.value = this.value;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  open(): void {
    if (!this.isDisabled && this.dp && !this.dp.isOpen()) {
      this.dp.open();
    }
  }

  onDateSelect(date: NgbDateStruct): void {
    const formatted: string = this.parserFormatter.format(date);
    this.updateValue(formatted);
    if (this.dp) {
      this.dp.close();
    }
  }

  onInput(): void {
    if (!this.inputRef) return;

    const input = this.inputRef.nativeElement as HTMLInputElement;
    const digits: string = input.value.replace(/\D/g, '').slice(0, 8);

    const parts: string[] = [
      digits.slice(0, 2), // MM
      digits.slice(2, 4), // DD
      digits.slice(4, 8), // YYYY
    ].filter(Boolean);

    const result: string = parts.join('/');
    input.value = result;

    if (FULL_RX.test(result)) {
      this.updateValue(result);
    }
  }

  onBlur(): void {
    if (!this.inputRef) return;

    const inputEl: HTMLInputElement = this.inputRef.nativeElement;
    const val: string = inputEl.value;

    if (this.dp?.isOpen()) return;

    this.markAsTouched();

    this.updateValue(val);
  }

  private updateValue(newValue: string): void {
    this.value = newValue;
    this.validateDate(newValue);
    this.onChange(newValue);
  }

  private validateDate(value: string): void {
    if (!value) {
      this.hasInvalidDate = false;
      return;
    }

    this.hasInvalidDate = !isValidDateString(value);
  }

  private markAsTouched(): void {
    if (!this.internalTouched) {
      this.internalTouched = true;
      this.onTouched();
    }
  }
}
